import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { postRequest } from "api/tools/httpRequests";
import { AxiosResponse } from "axios";
import {
  ICartQueueStatuses,
  IDBActivityCart,
} from "commonFnBcode/types/activityCart.types";
import { IJSONResponse } from "components/afterLogin/dashboard/flightApis/AirlineDiscountMarkup/types";
import { th } from "date-fns/locale";
import { toast } from "react-toastify";
import { RootState } from "redux/store";
import { ActivityCartService } from "services/activities/activityCart.service";

const initialState: {
  queueData: IDBActivityCart[] | undefined;
  statusCount: ICartQueueStatuses[] | undefined;
  activeStatus: string | null;
  queueLoading: boolean;
} = {
  queueData: undefined,
  statusCount: undefined,
  activeStatus: null,
  queueLoading: false,
};

const fetchQueueData = createAsyncThunk<
  void,
  {
    status: string | null;
    page: number;
  },
  {
    state: RootState;
  }
>("activities/fetchQueue", async (props, thunkAPI) => {
  thunkAPI.dispatch(activityQueueActions.setQueueLoading(true));
  try {
    const response: AxiosResponse<
      IJSONResponse<{
        carts: IDBActivityCart[];
        statuses: Array<ICartQueueStatuses>;
      }>
    > = await postRequest(`activities/queue/getQueue`, {
      status: props.status,
      page: props.page,
    });

    if (response.data.result) {
      thunkAPI.dispatch(
        activityQueueActions.setQueueData(response.data.result.carts)
      );
      thunkAPI.dispatch(
        activityQueueActions.setStatusCount(response.data.result.statuses)
      );
    }
  } catch (error) {
    toast.error("Error fetching activity search");
  }
  thunkAPI.dispatch(activityQueueActions.setQueueLoading(false));
});

const changeActiveStatus = createAsyncThunk<
  void,
  { status: string | null },
  {
    state: RootState;
  }
>("activities/changeActiveStatus", async (props, thunkAPI) => {
  try {
    thunkAPI.dispatch(activityQueueActions.setQueueData([]));
    thunkAPI.dispatch(activityQueueActions.setActiveStatus(props.status));
    thunkAPI.dispatch(
      fetchQueueData({
        page: 1,
        status: props.status,
      })
    );
  } catch (error) {
    toast.error("Error changing active status");
  }
});

const updateCartStatus = createAsyncThunk<
  void,
  { cartId: number; status: string },
  {
    state: RootState;
  }
>("activities/updateCartStatus", async (props, thunkAPI) => {
  thunkAPI.dispatch(activityQueueActions.setQueueLoading(true));
  try {
    const updateStatus = await ActivityCartService.updateCartStatus({
      cartId: props.cartId,
      status: props.status,
    });

    if (updateStatus) {
      thunkAPI.dispatch(
        fetchQueueData({
          page: 1,
          status: thunkAPI.getState().activityQueue.activeStatus,
        })
      );
    }
  } catch (error: any) {
    toast.error(error.message || "Error updating cart status");
  }
  // thunkAPI.dispatch(activityQueueActions.setQueueLoading(false));
});

export const ActivityQueueSlice = createSlice({
  name: "ActivityQueue",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setQueueData: (state, action: PayloadAction<IDBActivityCart[]>) => {
      state.queueData = action.payload;
    },
    setStatusCount: (state, action: PayloadAction<ICartQueueStatuses[]>) => {
      state.statusCount = action.payload;
    },
    setActiveStatus: (state, action: PayloadAction<string | null>) => {
      state.activeStatus = action.payload;
    },
    setQueueLoading: (state, action: PayloadAction<boolean>) => {
      state.queueLoading = action.payload;
    },
  },

  // extraReducers: {
  //   [fetchQueueData.pending.toString()]: (state) => {
  //     state.queueLoading = true;
  //   },
  //   [fetchQueueData.fulfilled.toString()]: (state) => {
  //     state.queueLoading = false;
  //   },
  //   [fetchQueueData.rejected.toString()]: (state) => {
  //     state.queueLoading = false;
  //   },
  // },
});

export const activityQueueActions = ActivityQueueSlice.actions;

export const activityQueueThunks = {
  fetchQueueData,
  updateCartStatus,
  changeActiveStatus,
};

export const selectActivityQueueSlice = (state: RootState) => {
  return state.activityQueue;
};

export default ActivityQueueSlice.reducer;
